import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

import { ACTIONS } from '../../copy';
import {
  NotificationContext,
  type Notification,
  type NotificationApi,
  type NotificationTone,
} from './notifications';

const DISMISS_AFTER_MS = 6_000;

/**
 * The notification surface: one live region, top right, above everything.
 *
 * `aria-live="polite"` rather than `assertive`, so a message waits for a
 * natural pause instead of interrupting whatever someone is reading. Each
 * notification is dismissible by hand as well as by timer -- a message that
 * vanishes on its own schedule and cannot be recalled is a message half the
 * people who needed it never read.
 */
export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<readonly Notification[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setNotifications((current) => current.filter((notification) => notification.id !== id));
  }, []);

  const notify = useCallback(
    (tone: NotificationTone, message: string) => {
      nextId.current += 1;
      const id = `n${String(nextId.current)}`;
      setNotifications((current) => [...current, { id, tone, message }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), DISMISS_AFTER_MS),
      );
    },
    [dismiss],
  );

  const api = useMemo<NotificationApi>(() => ({ notify, dismiss }), [notify, dismiss]);

  return (
    <NotificationContext value={api}>
      {children}
      <div className="notifications" role="region" aria-live="polite" aria-label="Avisos">
        {notifications.map((notification) => (
          <div key={notification.id} className={`notification notification--${notification.tone}`}>
            <p className="notification__message">{notification.message}</p>
            <button
              type="button"
              className="notification__dismiss"
              onClick={() => dismiss(notification.id)}
              aria-label={ACTIONS.close}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </NotificationContext>
  );
}
